import HelpAndSupportContext from "@/contexts/HelpAndSupportContext";
import Actions from "@/elements/Actions";
import axiosInterceptorInstance from "@/elements/axiosInterceptorInstance";
import MetaSection from "@/elements/MetaSection/MetaSection";
import { Mixpanel } from "@/elements/Mixpanel";
import { capitalized, cn } from "@/lib/utils";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { Fragment, useEffect, useState } from "react";
import { AiOutlineCloseCircle, AiOutlineSearch } from "react-icons/ai";

const helpTopics = [
    { title: "Univest Pro", route: "/help-and-support/univest-pro" },
    { title: "General", route: "/help-and-support/general" },
    { title: "Trade Ideas", route: "/help-and-support/trade-ideas" },
    { title: "Portfolio", route: "/help-and-support/portfolio" },
    { title: "Rewards", route: "/help-and-support/rewards" },
    { title: "Univest Elite", route: "/help-and-support/univest-elite" }
];

const dematHelpTopics = [
    { title: "Top Searched", route: "/help-and-support/top-searched-faqs" },
    { title: "Account Opening, KYC & Charges", route: "/help-and-support/demat-account" },
    { title: "Account Modification", route: "/help-and-support/account-modification" },
    { title: "Funds or Ledger", route: "/help-and-support/funds-or-ledger" },
    { title: "Place/Modify Orders", route: "/help-and-support/place-modify-orders" },
    { title: "AMO & GTT Orders", route: "/help-and-support/amo-gtt-orders" },
    { title: "Reports", route: "/help-and-support/reports" },
    { title: "Freeze Unfreeze Account", route: "/help-and-support/freeze-and-unfreeze" },
    { title: "Other", route: "/help-and-support/other" }
];

export default function Index({ isPaidUserPage }) {
    const router = useRouter();
    const [wordEntered, setWordEntered] = useState("");
    const [dematAccount, setDematAccount] = useState(false);
    const [faqList, setFaqList] = useState([]);

    useEffect(() => {
        Mixpanel.pageView({
            page: "help_and_support"
        });
    });

    useEffect(() => {
        if (wordEntered.length >= 3) {
            axiosInterceptorInstance.get(`/resources/web/faq-contents/search?searchText=${wordEntered}`).then((res) => {
                setFaqList(res.data?.message ? [] : res.data);
            });
        }
    }, [wordEntered]);

    return (
        <HelpAndSupportContext isPaidUserPage={isPaidUserPage}>
            <MetaSection
                title="Help and Support"
                desc="Get answers to frequently asked questions about our services and features."
            />
            {!dematAccount && (
                <>
                    <div
                        className={cn("flex flex-col items-start gap-4 self-stretch pb-6 ", {
                            "pt-1": isPaidUserPage
                        })}
                    >
                        <div className="self-stretch text-[#202020] font-Inter text-xl font-bold">
                            How can we help you?
                        </div>
                        <div className=" w-full font-Inter flex flex-row justify-start bg-white relative rounded-full">
                            <AiOutlineSearch
                                size="20px"
                                color="#202020"
                                className="absolute md:left-4 left-8 top-2.5"
                            />
                            <input
                                className="w-full focus:caret-[#00439D] rounded-full h-[40px] pl-12 pr-4 text-[12px] leading-[16px] font-medium text-[#202020] border bg-[#00000000] border-[#333333]"
                                type="text"
                                value={wordEntered}
                                placeholder="Search topic..."
                                onChange={(e) => {
                                    setWordEntered(e.target.value);
                                }}
                                onClick={() => {
                                    Mixpanel.track("search_clicked ", {
                                        page: "help_and_support"
                                    });
                                }}
                            />

                            {wordEntered?.length !== 0 && (
                                <AiOutlineCloseCircle
                                    className="absolute right-4 top-2.5"
                                    size="20px"
                                    color="#202020"
                                    onClick={() => setWordEntered("")}
                                />
                            )}
                        </div>
                    </div>
                    {wordEntered.length < 3 && (
                        <div>
                            <div className="flex justify-between pb-4">
                                <p className="self-stretch text-[#202020] font-Inter text-base font-bold leading-7">
                                    Top questions
                                </p>
                                <div
                                    className="font-Inter leading-[20px] text-[14px] font-semibold text-primary cursor-pointer flex items-center gap-1"
                                    onClick={() => {
                                        Mixpanel.track("cta_clicked", {
                                            page: "help_and_support",
                                            widget: "top_questions",
                                            cta_text: "view_all"
                                        });
                                        router?.push("/help-and-support/top-questions");
                                    }}
                                >
                                    View all
                                    <ChevronRight className="w-4 h-4 text-primary" />
                                </div>
                            </div>

                            {Actions.getCookie("auth_token") && <VideoList filter={null} />}
                        </div>
                    )}
                </>
            )}
            {wordEntered.length < 3 && (
                <div
                    className={cn("flex flex-col items-start gap-3 self-stretch pb-6 px-0", {
                        "pt-2": isPaidUserPage
                    })}
                >
                    {dematAccount ? (
                        <>
                            {!isPaidUserPage && (
                                <div className=" text-[#0862BC] font-Inter text-sm font-semibold leading-6 pb-4">
                                    {`Home > Demat`}
                                </div>
                            )}
                            <p className="text-[#414141] font-Inter text-base font-bold -mt-2 mb-2 ">Demat</p>
                        </>
                    ) : (
                        <p className="self-stretch text-[#202020] font-Inter text-base font-bold leading-7">
                            Help Topics
                        </p>
                    )}
                    {(dematAccount ? dematHelpTopics : helpTopics).map((topic, index) => (
                        <Fragment key={index}>
                            <div className="flex flex-col items-start gap-3 self-stretch">
                                <div
                                    className="flex justify-between items-center self-stretch cursor-pointer"
                                    onClick={() => {
                                        if (dematAccount) {
                                            Mixpanel.track("option_clicked", {
                                                page: "help_and_support",
                                                category: "demat",
                                                option_text: "demat",
                                                sub_category: topic.title
                                            });
                                        }
                                        Mixpanel.track("option_clicked", {
                                            page: "help_and_support",
                                            option_text: topic.title,
                                            category: topic.title
                                        });
                                        router?.push(topic.route);
                                    }}
                                >
                                    <p className="text-[#414141] font-Inter text-sm font-medium leading-6">
                                        {topic.title}
                                    </p>
                                    <ChevronRight color="#414141" className="w-6 h-6" />
                                </div>
                                <div className="h-[0.5px] self-stretch bg-[#DFDFDF] rounded-[50px]"></div>
                            </div>
                            {!dematAccount && index === 0 && (
                                <div className="flex flex-col items-start gap-3 self-stretch">
                                    <div
                                        className="flex justify-between items-center self-stretch cursor-pointer"
                                        onClick={() => {
                                            Mixpanel.track("option_clicked", {
                                                page: "help_and_support",
                                                option_text: "demat_account"
                                            });
                                            setDematAccount(true);
                                        }}
                                    >
                                        <p className="text-[#414141] font-Inter text-sm font-medium leading-6">
                                            Demat Account
                                        </p>
                                        <ChevronRight color="#414141" className="w-6 h-6" />
                                    </div>
                                    <div className="h-[0.5px] self-stretch bg-[#DFDFDF] rounded-[50px]"></div>
                                </div>
                            )}
                        </Fragment>
                    ))}
                </div>
            )}
            {wordEntered.length >= 3 && (
                <>
                    {Actions.getCookie("auth_token") && <VideoList filter={wordEntered} />}
                    {faqList?.map((ele, index) => (
                        <div
                            key={index}
                            className="flex justify-between items-center self-stretch cursor-pointer py-2 border-b border-[#DFDFDF]"
                            onClick={() =>
                                router?.push(
                                    `/help-and-support/${ele?.category?.replaceAll("_", "-")?.toLowerCase()}/${
                                        ele.slug
                                    }`
                                )
                            }
                        >
                            <p className="text-[#414141] font-Inter text-sm font-medium leading-6">{ele.question}</p>
                            <ChevronRight className="w-6 h-6" />
                        </div>
                    ))}
                </>
            )}
        </HelpAndSupportContext>
    );
}

export function VideoList({ filter }) {
    const [data, setData] = useState([]);

    useEffect(() => {
        axiosInterceptorInstance.get(`/resources/users/modules/banner/HELP_AND_SUPPORT_VIDEO`).then((res) => {
            setData(res?.data?.data?.list ?? []);
        });
    }, []);
    return (
        <div className="overflow-x-auto no-scrollbar flex flex-row gap-4">
            {data
                ?.filter((ele) => (filter ? ele.text?.includes(filter?.toLowerCase()?.replace(/ /g, "_")) : true))
                .map((ele, i) => (
                    <Link
                        key={i}
                        href={ele.url}
                        rel="noopener noreferrer"
                        className={`min-w-[134px] w-[134px] rounded-xl tilesBG overflow-hidden`}
                        target="_blank"
                        onClick={() => {
                            Mixpanel.track("banner_clicked", {
                                page: "help_and_support",
                                widget: "top_questions",
                                depth: `ad${i + 1}`,
                                cta_text: ele.text
                            });
                        }}
                    >
                        <img src={ele.imageURL} className="object-contain w-full mb-6" alt={ele.text} />
                    </Link>
                ))}
        </div>
    );
}

export async function getServerSideProps({ query, req }) {
    const { params } = query;

    const requests = [
        axios.get(`${process.env.apiBaseURL}/resources/web/footer-sections`).catch(() => ({ data: null }))
    ];

    const [footerContentRes] = await Promise.all(requests);

    return {
        props: {
            footerContentData: footerContentRes?.data ?? null,
            isPaidUserPage: !!req.cookies["auth_token"]
        }
    };
}
